const SELECTOR = "[data-reveal]";
const CLASS_REVEALED = "is-revealed";

const defaultOptions = {
  /** Slight inset from bottom so animation starts before the element is fully inside the viewport */
  rootMargin: "0px 0px -8% 0px",
  threshold: 0,
};

function bucketKey(rootMargin, threshold) {
  return `${rootMargin}\0${threshold}`;
}

/**
 * Adds `.is-revealed` when a `[data-reveal]` node intersects the viewport (once).
 * Optional: `data-reveal-margin="0px 0px -35% 0px"` (stricter bottom inset = start later).
 * Optional: `data-reveal-threshold="0.25"` (fraction of element that must be visible).
 * Skips observation when `prefers-reduced-motion: reduce` — reveals all immediately.
 */
export function initScrollReveal(options = {}) {
  const merged = { ...defaultOptions, ...options };
  const nodes = document.querySelectorAll(SELECTOR);
  if (!nodes.length) return;

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  if (reducedMotion) {
    nodes.forEach((el) => el.classList.add(CLASS_REVEALED));
    return;
  }

  const buckets = new Map();

  for (const el of nodes) {
    const rootMargin =
      el.getAttribute("data-reveal-margin") ?? merged.rootMargin;
    const thresholdAttr = el.getAttribute("data-reveal-threshold");
    const threshold =
      thresholdAttr != null && thresholdAttr !== ""
        ? Number(thresholdAttr)
        : merged.threshold;
    const key = bucketKey(rootMargin, threshold);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(el);
  }

  for (const [, elements] of buckets) {
    const first = elements[0];
    const rootMargin =
      first.getAttribute("data-reveal-margin") ?? merged.rootMargin;
    const thresholdAttr = first.getAttribute("data-reveal-threshold");
    const threshold =
      thresholdAttr != null && thresholdAttr !== ""
        ? Number(thresholdAttr)
        : merged.threshold;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add(CLASS_REVEALED);
          io.unobserve(entry.target);
        }
      },
      { root: null, rootMargin, threshold },
    );

    for (const el of elements) io.observe(el);
  }
}
